import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";

interface MarkdownRendererProps {
  content: string;
  className?: string;
  compact?: boolean;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = "",
  compact = false,
}) => {
  return (
    <div
      className={`markdown-content ${compact ? "markdown-compact" : ""} ${className}`}
      style={{ 
        wordBreak: 'break-word', 
        overflowWrap: 'anywhere',
        whiteSpace: 'pre-wrap'
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          // Headings
          h1: ({ children, ...props }) => (
            <h1
              className={`${compact ? "text-sm" : "text-lg"} font-bold text-white mb-2 border-b border-gray-600 pb-1`}
              {...props}
            >
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2
              className={`${compact ? "text-sm" : "text-base"} font-semibold text-white mb-2`}
              {...props}
            >
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3
              className={`${compact ? "text-xs" : "text-sm"} font-semibold text-gray-200 mb-1`}
              {...props}
            >
              {children}
            </h3>
          ),
          h4: ({ children, ...props }) => (
            <h4
              className={`${compact ? "text-xs" : "text-sm"} font-medium text-gray-300 mb-1`}
              {...props}
            >
              {children}
            </h4>
          ),
          h5: ({ children, ...props }) => (
            <h5
              className={`${compact ? "text-xs" : "text-sm"} font-medium text-gray-400 mb-1`}
              {...props}
            >
              {children}
            </h5>
          ),
          h6: ({ children, ...props }) => (
            <h6
              className={`${compact ? "text-xs" : "text-sm"} font-medium text-gray-400 mb-1`}
              {...props}
            >
              {children}
            </h6>
          ),

          // Paragraphs
          p: ({ children, ...props }) => (
            <p
              className={`text-gray-200 mb-2 leading-relaxed ${compact ? "text-xs leading-tight mb-1" : "text-sm"}`}
              {...props}
            >
              {children}
            </p>
          ),

          // Lists
          ul: ({ children, ...props }) => (
            <ul
              className={`list-disc list-inside text-gray-200 mb-2 space-y-1 ${compact ? "text-xs mb-1" : "text-sm"}`}
              {...props}
            >
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol
              className={`list-decimal list-inside text-gray-200 mb-2 space-y-1 ${compact ? "text-xs mb-1" : "text-sm"}`}
              {...props}
            >
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li className="text-gray-200" {...props}>
              {children}
            </li>
          ),

          // Code
          code: ({ children, className, ...props }: any) => {
            const inline = (props as any).inline;
            if (inline) {
              return (
                <code
                  className={`bg-gray-700 text-cyan-300 px-1 py-0.5 rounded text-xs font-mono ${compact ? "text-xs" : ""}`}
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <code
                className={`block bg-gray-800 text-cyan-300 p-2 rounded font-mono overflow-x-auto whitespace-pre-wrap ${compact ? "text-xs p-1" : "text-sm"} ${className}`}
                {...props}
              >
                {children}
              </code>
            );
          },
          pre: ({ children, ...props }) => (
            <pre
              className={`bg-gray-800 rounded mb-2 overflow-x-auto ${compact ? "mb-1" : ""}`}
              {...props}
            >
              {children}
            </pre>
          ),

          // Links
          a: ({ children, href, ...props }) => (
            <a
              href={href}
              className="text-blue-400 hover:text-blue-300 underline"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {children}
            </a>
          ),

          // Emphasis
          strong: ({ children, ...props }) => (
            <strong className="font-bold text-white" {...props}>
              {children}
            </strong>
          ),
          em: ({ children, ...props }) => (
            <em className="italic text-gray-300" {...props}>
              {children}
            </em>
          ),

          // Blockquotes
          blockquote: ({ children, ...props }) => (
            <blockquote
              className={`border-l-4 border-gray-500 pl-4 italic text-gray-300 mb-2 ${compact ? "text-xs mb-1 pl-2" : "text-sm"}`}
              {...props}
            >
              {children}
            </blockquote>
          ),

          // Tables
          table: ({ children, ...props }) => (
            <div className="overflow-x-auto mb-2">
              <table
                className={`min-w-full border-collapse border border-gray-600 ${compact ? "text-xs" : "text-sm"}`}
                {...props}
              >
                {children}
              </table>
            </div>
          ),
          thead: ({ children, ...props }) => (
            <thead className="bg-gray-700" {...props}>
              {children}
            </thead>
          ),
          tbody: ({ children, ...props }) => (
            <tbody {...props}>
              {children}
            </tbody>
          ),
          tr: ({ children, ...props }) => (
            <tr className="border-b border-gray-600" {...props}>
              {children}
            </tr>
          ),
          th: ({ children, ...props }) => (
            <th
              className="border border-gray-600 px-2 py-1 text-left font-semibold text-white"
              {...props}
            >
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td
              className="border border-gray-600 px-2 py-1 text-gray-200"
              {...props}
            >
              {children}
            </td>
          ),

          // Horizontal rule
          hr: ({ ...props }) => (
            <hr className="border-gray-600 mb-2" {...props} />
          ),

          // Images
          img: ({ src, alt, ...props }) => (
            <img
              src={src}
              alt={alt}
              className={`max-w-full h-auto rounded mb-2 ${compact ? "mb-1" : ""}`}
              {...props}
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
